import { computed, ref, type ComputedRef, type Ref } from 'vue'
import { graphql } from '@/shared/graphql/router'
import type {
  MatchCategoryBreakdownQuery,
  MatchCategoryType,
} from '@/shared/graphql/router/graphql'
// Resolves `...MatchBreakdown` below by name — the fragment itself is
// defined and exported from Match.vue, no import needed here (same as
// InfringementReportView.vue's existing `matches` query).

export const MATCH_CATEGORY_PAGE_SIZE = 20

export const MATCH_CATEGORY_BREAKDOWN = graphql(`
  query MatchCategoryBreakdown(
    $id: ID!
    $type: MatchCategoryType!
    $first: Int
    $after: String
  ) {
    report(id: $id) {
      id
      matches {
        categories(type: $type) {
          type
          total
          breakdown(input: { first: $first, after: $after }) {
            edges {
              cursor
              node {
                ...MatchBreakdown
              }
            }
            pageInfo {
              endCursor
              hasNextPage
            }
          }
        }
      }
    }
  }
`)

type MatchCategoryPage = NonNullable<
  NonNullable<
    MatchCategoryBreakdownQuery['report']
  >['matches']['categories'][number]
>

/**
 * Exposes one category from the initial Report query and requests subsequent
 * pages using Explorer's `hasNextPage`/`endCursor` loading guards.
 */
export const useMatchCategoryMatches = (
  type: MatchCategoryType,
  category: ComputedRef<MatchCategoryPage | undefined>,
  loading: Ref<boolean>,
  fetchNextPage: (type: MatchCategoryType, after: string) => Promise<unknown>
) => {
  const edges = computed(() => category.value?.breakdown.edges ?? [])
  const pageInfo = computed(() => category.value?.breakdown.pageInfo)
  const hasMore = computed(() => !!pageInfo.value?.hasNextPage)

  const loadingMore = ref(false)
  const loadMore = async () => {
    const endCursor = pageInfo.value?.endCursor
    if (!hasMore.value || !endCursor || loadingMore.value || loading.value) {
      return
    }

    loadingMore.value = true
    try {
      await fetchNextPage(type, endCursor)
    } finally {
      loadingMore.value = false
    }
  }

  return { edges, hasMore, loadingMore, loadMore, loading }
}
